import { useState, type KeyboardEvent } from 'react';
import { Check, Copy, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/useToast';
import { formatVariablePlaceholder } from '@/lib/admin/email-templates/models/email-template.model';

interface AvailableVariablesPanelProps {
  variables: string[];
  onChange: (variables: string[]) => void;
  /** Inserisce il placeholder nell'ultimo campo di testo toccato. False = nessun campo attivo. */
  onInsert: (placeholder: string) => boolean;
}

/**
 * Pannello delle variabili disponibili del template (`available_variables`).
 *
 * Ogni variabile è al tempo stesso: un pulsante che inserisce `{nome}` alla
 * posizione del cursore nell'ultimo campo di testo focalizzato, una icona per
 * copiare il placeholder negli appunti e una X per rimuoverla dalla lista.
 * I placeholder usano graffe SINGOLE, come si aspetta il mailer.
 */
export const AvailableVariablesPanel = ({
  variables,
  onChange,
  onInsert,
}: AvailableVariablesPanelProps) => {
  const { t } = useI18n();
  const { toast } = useToast();
  const [newVariable, setNewVariable] = useState('');
  const [copiedVariable, setCopiedVariable] = useState<string | null>(null);

  const handleAdd = () => {
    const value = newVariable.trim().replace(/[{}]/g, '');
    if (!value) return;
    if (variables.includes(value)) {
      setNewVariable('');
      return;
    }
    onChange([...variables, value]);
    setNewVariable('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Enter aggiunge senza inviare la form della modale.
    if (event.key !== 'Enter') return;
    event.preventDefault();
    handleAdd();
  };

  const handleRemove = (variable: string) => {
    onChange(variables.filter((existing) => existing !== variable));
  };

  const handleCopy = async (variable: string) => {
    const placeholder = formatVariablePlaceholder(variable);
    try {
      await navigator.clipboard.writeText(placeholder);
      setCopiedVariable(variable);
      window.setTimeout(() => setCopiedVariable(null), 1500);
    } catch (error) {
      console.error('Error copying variable to clipboard:', error);
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: t('emailTemplates.variables.copyError'),
      });
    }
  };

  const handleInsert = (variable: string) => {
    const inserted = onInsert(formatVariablePlaceholder(variable));
    if (inserted) return;
    toast({
      variant: 'info',
      title: t('emailTemplates.variables.noTargetTitle'),
      description: t('emailTemplates.variables.noTargetDescription'),
    });
  };

  return (
    <div className="flex h-full min-h-0 flex-col rounded-lg border border-gray-200 bg-gray-50">
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2">
        <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t('emailTemplates.variables.title')}
        </Label>
        <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-600">
          {variables.length}
        </span>
      </div>

      <p className="px-3 pt-2 text-xs text-gray-500">
        {t('emailTemplates.variables.hint')}
      </p>

      <div className="min-h-0 flex-1 overflow-y-auto p-3 max-h-64 lg:max-h-none">
        {variables.length === 0 ? (
          <p className="py-6 text-center text-xs text-gray-400">
            {t('emailTemplates.variables.empty')}
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {variables.map((variable) => (
              <li
                key={variable}
                className="flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1"
              >
                <button
                  type="button"
                  onClick={() => handleInsert(variable)}
                  title={t('emailTemplates.variables.insertAction')}
                  className="min-w-0 flex-1 truncate text-left font-mono text-xs text-gray-800 hover:text-blue-700"
                >
                  {formatVariablePlaceholder(variable)}
                </button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 shrink-0 p-0 text-gray-500 hover:text-gray-900"
                  onClick={() => handleCopy(variable)}
                  aria-label={t('emailTemplates.variables.copyAction')}
                  title={t('emailTemplates.variables.copyAction')}
                >
                  {copiedVariable === variable ? (
                    <Check className="h-3.5 w-3.5 text-green-600" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 shrink-0 p-0 text-gray-400 hover:text-red-600"
                  onClick={() => handleRemove(variable)}
                  aria-label={t('emailTemplates.variables.removeAction')}
                  title={t('emailTemplates.variables.removeAction')}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-gray-200 p-2">
        <Input
          value={newVariable}
          onChange={(event) => setNewVariable(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('emailTemplates.variables.addPlaceholder')}
          className="h-8 bg-white text-xs"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 shrink-0 px-2"
          onClick={handleAdd}
          disabled={!newVariable.trim()}
          aria-label={t('emailTemplates.variables.addAction')}
          title={t('emailTemplates.variables.addAction')}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};
